import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  TextInput,
  Button,
  ScrollArea,
  Box,
  Loader,
  Group,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconSearch, IconCheck } from '@tabler/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import {
  addIdeaPageColumn,
  type AvailableColumnSourceItem,
  getIdeaPages,
} from '../../../api/idea';
import { useAvailableColumnSources } from '../../../hooks/useIdeas';
import { ideaKeys } from '../../../hooks/ideaPage/ideaKeys';
import { ideaPagesKeys } from '../../../hooks/useIdeaPages';

export interface AddColumnModalProps {
  opened: boolean;
  onClose: () => void;
  /** Current table columns — used to hide sources already on the page. */
  existingColumns: any[];
  /** Ideas sub-page key (e.g. same value used for edit page / available-sources). */
  pageKey: string;
  /** After a successful add: await refetch (e.g. invalidateSubPageCache) then UI closes. */
  onColumnAdded?: () => void | Promise<void>;
}

const sourceItemKey = (item: AvailableColumnSourceItem) =>
  `${item.accessor}|${item.renderComponent || ''}|${item.source || ''}`;

const columnAlreadyPresent = (item: AvailableColumnSourceItem, existingColumns: any[]) =>
  existingColumns.some(
    (c: any) =>
      c.accessor === item.accessor &&
      (c.renderComponent || '') === (item.renderComponent || '')
  );

const matchesSearch = (item: AvailableColumnSourceItem, q: string) => {
  if (!q) return true;
  const hay = `${item.defaultTitle} ${item.accessor} ${item.dataType || ''} ${item.source || ''}`.toLowerCase();
  return hay.includes(q);
};

interface SourceSectionProps {
  title: string;
  items: AvailableColumnSourceItem[];
  search: string;
  selectedKey: string | null;
  onSelect: (item: AvailableColumnSourceItem) => void;
}

const SourceSection: React.FC<SourceSectionProps> = ({
  title,
  items,
  search,
  selectedKey,
  onSelect,
}) => {
  const q = search.trim().toLowerCase();
  const filtered = items.filter((item) => matchesSearch(item, q));

  if (filtered.length === 0) {
    return (
      <Box>
        <Text fw={600} fz="sm" c="dark.7" mb={6}>
          {title}
        </Text>
        <Text size="sm" c="dimmed">
          {items.length === 0 ? 'No fields in this category' : 'No fields match your search'}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text fw={600} fz="sm" c="dark.7" mb={8}>
        {title}
      </Text>
      <Stack spacing={6}>
        {filtered.map((item) => {
          const key = sourceItemKey(item);
          const selected = selectedKey === key;
          return (
            <Box
              key={key}
              onClick={() => onSelect(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(item);
                }
              }}
              sx={{
                borderRadius: 8,
                padding: '10px 12px',
                cursor: 'pointer',
                border: selected ? '1px solid #5C5CEB' : '1px solid #e9ecef',
                backgroundColor: selected ? '#EEF0FA' : '#fff',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                  borderColor: selected ? '#5C5CEB' : '#dee2e6',
                  backgroundColor: selected ? '#EEF0FA' : '#f8f9fa',
                },
              }}
            >
              <Text size="sm" fw={500} c="dark.8">
                {item.defaultTitle}
              </Text>
              <Text size="xs" c="dimmed" mt={2}>
                {item.accessor}
                {item.dataType ? ` · ${item.dataType}` : ''}
              </Text>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

/**
 * Add a read-only display column: pick an unused source, optionally rename, confirm via API.
 */
export const AddColumnModal: React.FC<AddColumnModalProps> = ({
  opened,
  onClose,
  existingColumns,
  pageKey,
  onColumnAdded,
}) => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const resolvedPageKey = pageKey || 'all';

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 150);
  const [selected, setSelected] = useState<AvailableColumnSourceItem | null>(null);
  const [columnTitle, setColumnTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, isError, error, refetch } = useAvailableColumnSources(resolvedPageKey);

  useEffect(() => {
    if (!opened) {
      setSearch('');
      setSelected(null);
      setColumnTitle('');
      setSubmitting(false);
    }
  }, [opened]);

  useEffect(() => {
    if (selected) {
      setColumnTitle(selected.defaultTitle);
    }
  }, [selected]);

  const ideaFields = useMemo(() => {
    const basic = data?.basicIdeaFields ?? [];
    const mapped = data?.mappedIdeaFields ?? [];
    return [...basic, ...mapped].filter((item) => !columnAlreadyPresent(item, existingColumns));
  }, [data, existingColumns]);

  const productItemFields = useMemo(
    () =>
      (data?.productItemFields ?? []).filter((item) => !columnAlreadyPresent(item, existingColumns)),
    [data, existingColumns]
  );

  const propertyFields = useMemo(
    () => (data?.properties ?? []).filter((item) => !columnAlreadyPresent(item, existingColumns)),
    [data, existingColumns]
  );

  const selectedKey = selected ? sourceItemKey(selected) : null;

  const handleConfirm = useCallback(async () => {
    if (!selected) return;
    const title = columnTitle.trim() || selected.defaultTitle;
    setSubmitting(true);
    try {
      await addIdeaPageColumn(auth0, resolvedPageKey, {
        accessor: selected.accessor,
        title,
        renderComponent: selected.renderComponent,
        source: selected.source,
        dataType: selected.dataType,
        readOnly: true,
      });
      await queryClient.fetchQuery({
        queryKey: ideaPagesKeys.all,
        queryFn: async () => {
          const response = await getIdeaPages(auth0);
          return response.ideaPages;
        },
      });
      await queryClient.invalidateQueries({
        queryKey: ideaKeys.availableColumnSources(resolvedPageKey),
      });
      await Promise.resolve(onColumnAdded?.());
      onClose();
      showNotification({
        title: 'Column added',
        color: 'teal',
        message: `"${title}" is now on this page.`,
        icon: <IconCheck size="1.1rem" />,
        autoClose: 2500,
      });
    } catch (e) {
      console.error(e);
      showNotification({
        title: 'Could not add column',
        color: 'red',
        message: 'Please try again or contact support if this persists.',
        autoClose: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  }, [auth0, columnTitle, onClose, onColumnAdded, queryClient, resolvedPageKey, selected]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} fz="lg" c="dark.8">
          Add column
        </Text>
      }
      centered
      size="lg"
      styles={{
        body: { paddingTop: 8 },
      }}
    >
      <Stack spacing="md">
        <TextInput
          placeholder="Search fields"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          icon={<IconSearch size={16} color="#616161" />}
          size="sm"
          styles={{
            input: {
              borderRadius: 8,
              border: '1px solid #e0e0e0',
            },
          }}
        />

        {isLoading && (
          <Group position="center" py="xl">
            <Loader size="sm" />
          </Group>
        )}

        {isError && (
          <Stack spacing="xs">
            <Text size="sm" c="red">
              {(error as Error)?.message || 'Failed to load available fields.'}
            </Text>
            <Button variant="light" onClick={() => refetch()}>
              Retry
            </Button>
          </Stack>
        )}

        {!isLoading && !isError && data && (
          <ScrollArea h={320} offsetScrollbars>
            <Stack spacing="lg" pr={4}>
              <SourceSection
                title="Idea Fields"
                items={ideaFields}
                search={debouncedSearch}
                selectedKey={selectedKey}
                onSelect={setSelected}
              />
              <SourceSection
                title="Product Item Fields"
                items={productItemFields}
                search={debouncedSearch}
                selectedKey={selectedKey}
                onSelect={setSelected}
              />
              <SourceSection
                title="Properties"
                items={propertyFields}
                search={debouncedSearch}
                selectedKey={selectedKey}
                onSelect={setSelected}
              />
            </Stack>
          </ScrollArea>
        )}

        <TextInput
          label="Column name"
          description="Shown as the table header. Defaults to the field label; you can override it."
          placeholder="Column title"
          value={columnTitle}
          onChange={(e) => setColumnTitle(e.currentTarget.value)}
          disabled={!selected}
          size="sm"
          styles={{
            input: { borderRadius: 8 },
          }}
        />

        <Group position="right" mt="xs">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            loading={submitting}
            disabled={!selected || isLoading}
            sx={{ fontWeight: 500 }}
          >
            Add column
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
